import { computed, ref, type ComputedRef, type Ref } from 'vue';

/**
 * Gmail-style multiselect state for charging-station tables.
 *
 * Two mutually-exclusive modes:
 * - `ids`: explicit checkbox ticks. Unions across pages and across filter/search
 *   changes (building a custom set), expressible as an ID list.
 * - `filter`: "select all Y matching the current filter". Represented as a single
 *   filter string — it cannot also carry unrelated IDs — so entering it is
 *   exclusive (discards prior id picks), and a filter/search change invalidates
 *   it (clears to empty).
 *
 * The composable is UI-agnostic: methods that need the current page take the
 * visible rows as an argument so it stays pure and testable.
 */

export interface IdSelection<T> {
  mode: 'ids';
  rows: T[];
}

export interface FilterSelection {
  mode: 'filter';
  filter: string;
  count: number;
  /** Rows the user unticked after "select all matching": the selection is the filter minus these. */
  excludedIds: string[];
}

export type StationSelection<T> = IdSelection<T> | FilterSelection;

export interface UseStationSelection<T> {
  mode: ComputedRef<'ids' | 'filter'>;
  count: ComputedRef<number>;
  selection: ComputedRef<StationSelection<T>>;
  /** Keys excluded from a filter-mode selection; always empty in id-mode. */
  excludedKeys: ComputedRef<string[]>;
  isSelected: (row: T) => boolean;
  isAllOnScreenSelected: (visibleRows: T[]) => boolean;
  /** Replace the id-mode selection wholesale — mirrors an external selection array
   *  (e.g. the table's own `@selected` emit). Leaves filter-mode. */
  setSelectedRows: (rows: T[]) => void;
  toggle: (row: T) => void;
  selectAllOnScreen: (visibleRows: T[]) => void;
  deselectAllOnScreen: (visibleRows: T[]) => void;
  selectAllMatching: (filter: string, count: number) => void;
  notifyFilterChanged: () => void;
  clear: () => void;
}

export function useStationSelection<T>(getKey: (row: T) => string = (row) => (row as { id: string }).id): UseStationSelection<T> {
  const mode = ref<'ids' | 'filter'>('ids');
  // Insertion-ordered map of explicitly-selected rows (id-mode).
  const idRows: Ref<Map<string, T>> = ref(new Map());
  const filterState = ref<{ filter: string; count: number; excluded: Set<string> } | null>(null);

  const count = computed(() =>
    mode.value === 'filter' && filterState.value
      ? filterState.value.count - filterState.value.excluded.size
      : idRows.value.size
  );

  const selection = computed<StationSelection<T>>(() =>
    mode.value === 'filter' && filterState.value
      ? {
          mode: 'filter',
          filter: filterState.value.filter,
          count: filterState.value.count,
          excludedIds: [...filterState.value.excluded]
        }
      : { mode: 'ids', rows: [...idRows.value.values()] }
  );

  const excludedKeys = computed(() => (mode.value === 'filter' && filterState.value ? [...filterState.value.excluded] : []));


  function isExcluded(row: T): boolean {
    return filterState.value?.excluded.has(getKey(row)) ?? false;
  }

  function isSelected(row: T): boolean {
    if (mode.value === 'filter') return !isExcluded(row);
    return idRows.value.has(getKey(row));
  }

  function isAllOnScreenSelected(visibleRows: T[]): boolean {
    if (mode.value === 'filter') return !visibleRows.some(isExcluded);
    if (visibleRows.length === 0) return false;
    return visibleRows.every((row) => idRows.value.has(getKey(row)));
  }

  function enterIdMode(rows: T[]): void {
    mode.value = 'ids';
    filterState.value = null;
    idRows.value = new Map(rows.map((row) => [getKey(row), row]));
  }

  // Sets are replaced, never mutated: an in-place add would not re-run the computeds.
  function setExcluded(excluded: Set<string>): void {
    if (!filterState.value) return;
    if (excluded.size >= filterState.value.count) {
      // Excluding every match leaves nothing selected; don't keep a "0 selected" filter-mode.
      clear();
      return;
    }
    filterState.value = { ...filterState.value, excluded };
  }

  function toggle(row: T): void {
    if (mode.value === 'filter' && filterState.value) {
      // "All except": unticking keeps the filter and excludes the row (N -> N-1, not the visible page).
      const key = getKey(row);
      const excluded = new Set(filterState.value.excluded);
      if (excluded.has(key)) excluded.delete(key);
      else excluded.add(key);
      setExcluded(excluded);
      return;
    }
    const key = getKey(row);
    const next = new Map(idRows.value);
    if (next.has(key)) next.delete(key);
    else next.set(key, row);
    idRows.value = next;
  }

  function selectAllOnScreen(visibleRows: T[]): void {
    if (mode.value === 'filter' && filterState.value) {
      // Re-includes the visible rows the user excluded; never shrinks the filter selection.
      const excluded = new Set(filterState.value.excluded);
      visibleRows.forEach((row) => excluded.delete(getKey(row)));
      setExcluded(excluded);
      return;
    }
    const next = new Map(idRows.value);
    visibleRows.forEach((row) => next.set(getKey(row), row));
    idRows.value = next;
  }

  function deselectAllOnScreen(visibleRows: T[]): void {
    if (mode.value === 'filter') {
      // Unchecking the header while "all matching" is active deselects everything.
      enterIdMode([]);
      return;
    }
    const next = new Map(idRows.value);
    visibleRows.forEach((row) => next.delete(getKey(row)));
    idRows.value = next;
  }

  function selectAllMatching(filter: string, count: number): void {
    // Exclusive: filter-mode is a single filter selector and cannot carry
    // unrelated ids, so any prior id picks are discarded.
    mode.value = 'filter';
    idRows.value = new Map();
    filterState.value = { filter, count, excluded: new Set() };
  }

  function notifyFilterChanged(): void {
    // id-mode persists across filter changes (union set building);
    // filter-mode snapshot is invalidated and cleared.
    if (mode.value === 'filter') clear();
  }

  function clear(): void {
    mode.value = 'ids';
    idRows.value = new Map();
    filterState.value = null;
  }

  return {
    mode: computed(() => mode.value),
    count,
    selection,
    excludedKeys,
    isSelected,
    isAllOnScreenSelected,
    setSelectedRows: enterIdMode,
    toggle,
    selectAllOnScreen,
    deselectAllOnScreen,
    selectAllMatching,
    notifyFilterChanged,
    clear
  };
}
